import React from 'react';

import { Zone } from '@/services/api/zones';

interface ActiveZoneChipProps {
  zone: Zone | null;
  cellCount: number;
}

/**
 * Floating top-left chip on the map. Surfaces the running cell count of
 * the zone currently being edited, so ops doesn't need to look anywhere
 * else after a hex click.
 *
 * Compact form: single primary line with color dot + code + cell count,
 * one helper line below. Zone name is exposed via the title (hover
 * tooltip) rather than taking up a third visual line.
 */
const ActiveZoneChip: React.FC<ActiveZoneChipProps> = ({ zone, cellCount }) => {
  if (!zone) {
    return (
      <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 px-3 py-2 shadow-sm">
        <p className="text-[12px] tracking-tight text-text-sub-600">
          <span className="font-semibold text-text-strong-950">
            No zone selected.
          </span>{' '}
          Pick or create one on the right.
        </p>
      </div>
    );
  }

  return (
    <div
      className="max-w-[260px] rounded-2xl border border-stroke-soft-200 bg-bg-white-0 px-3 py-2 shadow-sm"
      title={zone.name}
    >
      <div className="flex items-center gap-2">
        <span
          className="h-3 w-3 shrink-0 rounded-full"
          style={{
            backgroundColor: zone.color,
            boxShadow: `0 0 0 2.5px ${zone.color}26`,
          }}
        />
        <span className="font-mono text-[12.5px] font-bold tracking-tight text-text-strong-950">
          {zone.code}
        </span>
        <span className="text-text-soft-400">·</span>
        <span className="font-mono text-[12px] font-bold tabular text-accent">
          {cellCount} {cellCount === 1 ? 'cell' : 'cells'}
        </span>
      </div>
      <p className="mt-1 text-[10.5px] text-text-sub-600">
        Click hex to remove · empty space to add
      </p>
    </div>
  );
};

export default ActiveZoneChip;
